/**
 * OrderWizardMSSP — Wizard multi-step per la creazione ordine MSSP.
 *
 * Step 1: Dati azienda (seleziona esistente o crea nuova)
 * Step 2: Selezione prodotto + configurazione
 * Step 3: Asset da monitorare
 * Step 4: Riepilogo + conferma
 *
 * NO pagamento (gestito lato MSSP).
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Send, Building2 } from 'lucide-react';
import useGet from '../../Hooks/useGet';
import usePost from '../../Hooks/usePost';
import API_ENDPOINTS from '../../Config/api';

import StepAziendaMSSP from './StepAziendaMSSP';
import StepProdottoMSSP from './StepProdottoMSSP';
import StepAssetMSSP from './StepAssetMSSP';
import StepRiepilogoMSSP from './StepRiepilogoMSSP';

const TOTAL_STEPS = 4;
const STEP_LABELS = ['Dati Azienda', 'Scegli Prodotto', 'Asset', 'Riepilogo'];

export default function OrderWizardMSSP({ onSubmitSuccess, onClose }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const { postData } = usePost();

  // ── Data fetching ──
  const { fetchedData: aziendeMSSP } = useGet(API_ENDPOINTS.myAziendeMSSP);
  const { fetchedData: listinoData } = useGet(API_ENDPOINTS.listinoProdotti);
  const { fetchedData: settoriData } = useGet(API_ENDPOINTS.getSettori);
  const [aziende, setAziende] = useState([]);

  useEffect(() => { if (aziendeMSSP) setAziende(aziendeMSSP); }, [aziendeMSSP]);

  const settori = useMemo(() => {
    if (!settoriData) return [];
    return Array.isArray(settoriData) ? settoriData : settoriData.results || [];
  }, [settoriData]);

  // ── Step 1: Azienda state ──
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [selectedAziendaId, setSelectedAziendaId] = useState(null);
  const [newAziendaData, setNewAziendaData] = useState({
    nome_azienda: '', email: '', partita_iva: '', codice_fiscale: '',
    pec: '', url: '', telefono: '', indirizzo: '', cap: '', citta: '',
    comune: '', provincia: '', stato: '', numero_dipendenti: '',
    fatturato_annuo: '', id_settore: '', codice_ateco: '',
  });

  // ── Step 2: Ordine/prodotto state ──
  const [ordineData, setOrdineData] = useState({
    listino_prodotto_base: null,
    tipo_selezione: 'singolo',
    numero_endpoint: '',
    offerta_sorveglianza_rete: '',
    awareness_enabled: false,
    awareness_seats: '',
    servizio_mssp_monitoring: false,
    gettoni_totali: '',
    ore_per_gettone: 2,
    // Step 3: Asset
    asset: [{ asset: '', tipo_asset: 'dominio' }],
  });

  // Dati azienda per il riepilogo
  const selectedAzienda = useMemo(() => {
    if (isCreatingNew) return newAziendaData;
    return aziende.find(a => a.id_azienda === selectedAziendaId) || {};
  }, [isCreatingNew, newAziendaData, aziende, selectedAziendaId]);

  // Prezzo calcolato (per passarlo al submit)
  const lastCalcRef = useRef('');
  const [importoCalcolato, setImportoCalcolato] = useState('0.00');

  useEffect(() => {
    if (!ordineData.numero_endpoint || !ordineData.listino_prodotto_base || !listinoData) return;
    const numEP = parseInt(ordineData.numero_endpoint);
    if (isNaN(numEP) || numEP < 1) return;

    const calcKey = `${ordineData.listino_prodotto_base}-${numEP}-${ordineData.offerta_sorveglianza_rete||'none'}-${ordineData.awareness_enabled}-${ordineData.awareness_seats||'0'}-${ordineData.servizio_mssp_monitoring}-${ordineData.gettoni_totali||'0'}`;
    if (lastCalcRef.current === calcKey) return;
    lastCalcRef.current = calcKey;

    const prodotto = listinoData.find(p => p.id === parseInt(ordineData.listino_prodotto_base) || p.id === ordineData.listino_prodotto_base);
    if (!prodotto) return;

    let tot = parseFloat(prodotto.prezzo_base);
    const isBundle = prodotto.tipo_prodotto === 'BUNDLE';

    if (isBundle) {
      if (prodotto.scalabile && prodotto.numero_endpoint > 0 && numEP > prodotto.numero_endpoint) {
        const extra = numEP - prodotto.numero_endpoint;
        tot += Math.ceil(extra / (prodotto.incremento_scalabilita || 10)) * parseFloat(prodotto.prezzo_scalabilita || 0);
      }
      if (ordineData.awareness_enabled && ordineData.awareness_seats) {
        const seats = parseInt(ordineData.awareness_seats);
        const seatsIncluse = prodotto.numero_awareness_seats || 10;
        if (seats > seatsIncluse) {
          const awrProd = listinoData.find(p => p.tipo_prodotto === 'AWARENESS' && p.attivo);
          if (awrProd?.scalabile) {
            tot += Math.ceil((seats - seatsIncluse) / (awrProd.incremento_scalabilita || 10)) * parseFloat(awrProd.prezzo_scalabilita || awrProd.prezzo_base);
          }
        }
      }
      if (ordineData.servizio_mssp_monitoring && ordineData.gettoni_totali) {
        const gett = parseInt(ordineData.gettoni_totali);
        const gettInclusi = prodotto.numero_gettoni || 20;
        if (gett > gettInclusi) {
          const msspProd = listinoData.find(p => p.tipo_prodotto === 'MSSP_MONITORING' && p.attivo);
          if (msspProd) {
            tot += (gett - gettInclusi) * (parseFloat(msspProd.prezzo_base) / (msspProd.numero_gettoni || 20));
          }
        }
      }
    } else {
      const epBase = prodotto.numero_endpoint || 10;
      if (prodotto.scalabile && numEP > epBase) {
        tot += Math.ceil((numEP - epBase) / (prodotto.incremento_scalabilita || 10)) * parseFloat(prodotto.prezzo_scalabilita || 0);
      }
      if (ordineData.offerta_sorveglianza_rete) {
        const nsProd = listinoData.find(p => p.tipo_prodotto === 'NETWORK_SURVEILLANCE' && p.attivo);
        if (nsProd) tot += parseFloat(nsProd.prezzo_base);
      }
      if (ordineData.awareness_enabled && ordineData.awareness_seats) {
        const awrProd = listinoData.find(p => p.tipo_prodotto === 'AWARENESS' && p.attivo);
        if (awrProd) {
          const seatsBase = awrProd.numero_awareness_seats || 10;
          tot += parseFloat(awrProd.prezzo_base);
          const seats = parseInt(ordineData.awareness_seats);
          if (seats > seatsBase && awrProd.scalabile) {
            tot += Math.ceil((seats - seatsBase) / (awrProd.incremento_scalabilita || 10)) * parseFloat(awrProd.prezzo_scalabilita || awrProd.prezzo_base);
          }
        }
      }
      if (ordineData.servizio_mssp_monitoring) {
        const msspProd = listinoData.find(p => p.tipo_prodotto === 'MSSP_MONITORING' && p.attivo);
        if (msspProd) {
          const gettoniBase = msspProd.numero_gettoni || 20;
          const gett = parseInt(ordineData.gettoni_totali) || gettoniBase;
          tot += (parseFloat(msspProd.prezzo_base) / gettoniBase) * gett;
        }
      }
    }

    setImportoCalcolato(tot.toFixed(2));
  }, [ordineData, listinoData]);

  // ── Validazione per step ──
  const validateStep = () => {
    const errs = {};

    if (step === 1) {
      if (isCreatingNew) {
        if (!newAziendaData.nome_azienda.trim()) errs['azienda.nome_azienda'] = 'Campo obbligatorio';
        if (!newAziendaData.partita_iva.trim()) errs['azienda.partita_iva'] = 'Campo obbligatorio';
        if (!newAziendaData.email.trim()) errs['azienda.email'] = 'Campo obbligatorio';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newAziendaData.email))
          errs['azienda.email'] = 'Email non valida';
        if (newAziendaData.codice_fiscale && newAziendaData.codice_fiscale.trim()) {
          const cf = newAziendaData.codice_fiscale.trim().toUpperCase();
          const isValidCF = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(cf);
          const isValidPIVA = /^\d{11}$/.test(cf);
          if (!isValidCF && !isValidPIVA) {
            errs['azienda.codice_fiscale'] = 'Codice fiscale non valido: 16 caratteri alfanumerici o 11 cifre';
          }
        }
      } else {
        if (!selectedAziendaId) errs['id_azienda'] = 'Seleziona un\'azienda';
      }
    }

    if (step === 2) {
      if (!ordineData.listino_prodotto_base) errs['listino_prodotto_base'] = 'Seleziona un prodotto';
      if (!ordineData.numero_endpoint || parseInt(ordineData.numero_endpoint) < 1)
        errs['numero_endpoint'] = 'Minimo 1 endpoint';
    }

    if (step === 3) {
      const validAssets = (ordineData.asset || []).filter(a => a.asset.trim());
      if (validAssets.length === 0) errs['asset'] = 'Inserisci almeno un asset';
      const seen = new Set();
      for (const a of validAssets) {
        const key = `${a.tipo_asset}:${a.asset.trim().toLowerCase()}`;
        if (seen.has(key)) {
          errs['asset'] = 'Rimuovi gli asset duplicati prima di continuare';
          break;
        }
        seen.add(key);
      }
    }

    setServerErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Navigazione ──
  const goNext = () => { if (validateStep() && step < TOTAL_STEPS) setStep(step + 1); };
  const goBack = () => { if (step > 1) setStep(step - 1); };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setServerErrors({});

    try {
      let aziendaId = selectedAziendaId;

      // Se sta creando una nuova azienda, crearla prima
      if (isCreatingNew) {
        const formDataToSend = new FormData();
        formDataToSend.append('nome_azienda', newAziendaData.nome_azienda);
        formDataToSend.append('email', newAziendaData.email);
        formDataToSend.append('partita_iva', newAziendaData.partita_iva || '');
        formDataToSend.append('codice_fiscale', newAziendaData.codice_fiscale || '');
        formDataToSend.append('pec', newAziendaData.pec || '');
        formDataToSend.append('url', newAziendaData.url || '');
        formDataToSend.append('indirizzo', newAziendaData.indirizzo || '');
        formDataToSend.append('cap', newAziendaData.cap || '');
        formDataToSend.append('citta', newAziendaData.citta || '');
        formDataToSend.append('comune', newAziendaData.comune || '');
        formDataToSend.append('provincia', newAziendaData.provincia || '');
        formDataToSend.append('stato', newAziendaData.stato || '');
        formDataToSend.append('telefono', newAziendaData.telefono || '');
        if (newAziendaData.numero_dipendenti)
          formDataToSend.append('numero_dipendenti', newAziendaData.numero_dipendenti);
        if (newAziendaData.fatturato_annuo)
          formDataToSend.append('fatturato_annuo', newAziendaData.fatturato_annuo);
        if (newAziendaData.id_settore)
          formDataToSend.append('id_settore', newAziendaData.id_settore);
        formDataToSend.append('codice_ateco', newAziendaData.codice_ateco || '');

        const aziendaResp = await postData(API_ENDPOINTS.myAziendeMSSP, formDataToSend);
        if (!aziendaResp?.id_azienda) {
          throw new Error('Errore nella creazione dell\'azienda');
        }
        aziendaId = aziendaResp.id_azienda;
        // Aggiorna lista aziende locali
        setAziende(prev => [...prev, aziendaResp]);
      }

      // Crea l'ordine
      const payload = {
        id_azienda: aziendaId,
        listino_prodotto_base: ordineData.listino_prodotto_base,
        numero_endpoint: ordineData.numero_endpoint,
        importo_euro: importoCalcolato,
        iva: '22',
        email_contatto: isCreatingNew ? newAziendaData.email : (selectedAzienda.email || selectedAzienda.email_contatto || selectedAzienda.email_riferimento || ''),
        telefono_contatto: isCreatingNew ? newAziendaData.telefono : (selectedAzienda.telefono || ''),
        offerta_sorveglianza_rete: ordineData.offerta_sorveglianza_rete || '',
        awareness_enabled: ordineData.awareness_enabled,
        awareness_seats: ordineData.awareness_enabled ? parseInt(ordineData.awareness_seats) : 0,
        servizio_mssp_monitoring: ordineData.servizio_mssp_monitoring || false,
        gettoni_totali: ordineData.servizio_mssp_monitoring ? (parseInt(ordineData.gettoni_totali) || 20) : 0,
        ore_per_gettone: ordineData.servizio_mssp_monitoring ? (parseInt(ordineData.ore_per_gettone) || 2) : 2,
        asset: (ordineData.asset || []).filter(a => a.asset.trim()).map(a => a.asset).join('\n'),
      };

      const response = await postData(API_ENDPOINTS.myOrdiniMSSP, payload);
      if (onSubmitSuccess) onSubmitSuccess(response);
    } catch (error) {
      console.error('Error creating order:', error);

      if (error.details) {
        const flat = {};
        Object.entries(error.details).forEach(([k, v]) => { flat[k] = v; });
        setServerErrors(flat);

        // Torna allo step appropriato
        if (flat['nome_azienda'] || flat['partita_iva'] || flat['email'] || flat['id_azienda']) {
          setStep(1);
        } else if (flat['listino_prodotto_base'] || flat['numero_endpoint']) {
          setStep(2);
        } else if (flat['asset']) {
          setStep(3);
        }
      } else {
        setServerErrors({ detail: error.message || 'Errore nella creazione dell\'ordine' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──
  return (
    <div className="h-full flex flex-col">
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          {STEP_LABELS.map((label, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 text-sm ${
                i + 1 <= step ? 'text-yellow-600 font-semibold' : 'text-gray-400'
              }`}
            >
              <span
                className={`
                  w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                  ${i + 1 < step
                    ? 'bg-yellow-600 text-white'
                    : i + 1 === step
                      ? 'bg-yellow-100 text-yellow-600 ring-2 ring-yellow-600'
                      : 'bg-gray-200 text-gray-500'}
                `}
              >
                {i + 1 < step ? '✓' : i + 1}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </div>
          ))}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-yellow-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Errori globali */}
      {serverErrors.detail && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {serverErrors.detail}
        </div>
      )}

      {/* Step content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 md:p-6 flex-1 min-h-0 overflow-hidden">
        {step === 1 && (
          <StepAziendaMSSP
            aziende={aziende}
            selectedAziendaId={selectedAziendaId}
            onSelectAzienda={setSelectedAziendaId}
            newAziendaData={newAziendaData}
            onChangeNewAzienda={setNewAziendaData}
            isCreatingNew={isCreatingNew}
            onToggleCreateNew={setIsCreatingNew}
            settori={settori}
            errors={serverErrors}
          />
        )}
        {step === 2 && (
          <StepProdottoMSSP
            data={ordineData}
            onChange={setOrdineData}
            listinoData={listinoData}
            errors={serverErrors}
          />
        )}
        {step === 3 && (
          <StepAssetMSSP
            data={ordineData}
            onChange={setOrdineData}
            errors={serverErrors}
          />
        )}
        {step === 4 && (
          <StepRiepilogoMSSP
            aziendaData={selectedAzienda}
            isCreatingNew={isCreatingNew}
            ordineData={ordineData}
            listinoData={listinoData}
            errors={serverErrors}
          />
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between mt-3 pb-1">
        {step > 1 ? (
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Annulla
          </button>
        )}

        {step < TOTAL_STEPS ? (
          <button
            type="button"
            onClick={goNext}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Avanti
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:bg-yellow-400 transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creazione in corso...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Conferma Ordine
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
